import { displayHost } from "../display-host";

export type SocialPlatform =
    | "facebook"
    | "instagram"
    | "youtube"
    | "x"
    | "pinterest"
    | "tiktok"
    | "threads"
    | "linkedin";

export type ProviderSocialLink = {
    href: string;
    platform: SocialPlatform | null;
    name: string;
    handle: string | null;
};

type Platform = {
    platform: SocialPlatform;
    name: string;
    domains: readonly string[];
    handlePrefix: "@" | "" | null;
    reservedPaths: ReadonlySet<string>;
};

const PLATFORMS: readonly Platform[] = [
    {
        platform: "facebook",
        name: "Facebook",
        domains: ["facebook.com", "fb.com"],
        handlePrefix: "",
        reservedPaths: new Set([
            "profile.php",
            "l.php",
            "pages",
            "people",
            "groups",
            "share",
            "home",
            "login",
            "events",
            "photos",
            "videos",
            "search",
            "marketplace",
            "about",
        ]),
    },
    {
        platform: "instagram",
        name: "Instagram",
        domains: ["instagram.com", "instagr.am"],
        handlePrefix: "@",
        reservedPaths: new Set(["p", "reel", "explore"]),
    },
    {
        platform: "youtube",
        name: "YouTube",
        domains: ["youtube.com"],
        handlePrefix: "@",
        reservedPaths: new Set(["channel", "c", "user", "watch"]),
    },
    {
        platform: "youtube",
        name: "YouTube",
        domains: ["youtu.be"],
        handlePrefix: null,
        reservedPaths: new Set(),
    },
    {
        platform: "x",
        name: "X",
        domains: ["twitter.com", "x.com"],
        handlePrefix: "@",
        reservedPaths: new Set([
            "intent",
            "i",
            "home",
            "search",
            "explore",
            "hashtag",
        ]),
    },
    {
        platform: "pinterest",
        name: "Pinterest",
        domains: ["pinterest.com"],
        handlePrefix: null,
        reservedPaths: new Set(),
    },
    {
        platform: "tiktok",
        name: "TikTok",
        domains: ["tiktok.com"],
        handlePrefix: "@",
        reservedPaths: new Set(["explore", "tag", "discover", "foryou"]),
    },
    {
        platform: "threads",
        name: "Threads",
        domains: ["threads.net", "threads.com"],
        handlePrefix: "@",
        reservedPaths: new Set(["search", "login"]),
    },
    {
        platform: "linkedin",
        name: "LinkedIn",
        domains: ["linkedin.com"],
        handlePrefix: null,
        reservedPaths: new Set(["company", "in"]),
    },
];

const HANDLE_PATTERN = /^@?[A-Za-z0-9._-]{2,}$/u;

function parseHandle(
    { handlePrefix, reservedPaths }: Platform,
    pathname: string,
): string | null {
    if (handlePrefix === null) return null;
    const segments = pathname.split("/").filter(Boolean);
    const [segment] = segments;
    if (segments.length !== 1 || !segment || !HANDLE_PATTERN.test(segment)) {
        return null;
    }
    const handle = segment.replace(/^@/u, "");
    if (reservedPaths.has(handle.toLowerCase())) return null;
    return `${handlePrefix}${handle}`;
}

function toSocialLink(href: string): ProviderSocialLink {
    const { hostname, pathname } = new URL(href);
    const host = displayHost(hostname);
    const match = PLATFORMS.find(({ domains }) =>
        domains.some(
            (domain) => host === domain || host.endsWith(`.${domain}`),
        ),
    );
    return {
        href,
        platform: match?.platform ?? null,
        name: match?.name ?? host,
        handle: match ? parseHandle(match, pathname) : null,
    };
}

export function toProviderSocialLinks(
    urls: readonly string[],
): ProviderSocialLink[] {
    const links = new Map<string, ProviderSocialLink>();
    for (const url of urls) {
        const link = toSocialLink(url);
        const key =
            link.platform && link.handle
                ? `${link.platform}|${link.handle.toLowerCase()}`
                : link.href;
        if (!links.has(key)) links.set(key, link);
    }
    return [...links.values()];
}
